'use client';

import { Menu, X } from 'lucide-react';
import Link from 'next/link';
import { useSession } from 'next-auth/react';
import { useEffect, useState } from 'react';
import dynamic from 'next/dynamic';
import { AnimatePresence, motion } from 'framer-motion';

// Dynamically import components that might have SSR issues
const MobileUserMenu = dynamic(() => import('../navbar/MobileUserMenu').then(mod => ({ default: mod.MobileUserMenu })), { ssr: false });
const UserMenu = dynamic(() => import('../navbar/UserMenu').then(mod => ({ default: mod.UserMenu })), { ssr: false });

const menuItems = [
  {
    href: '/used-cars',
    label: '買車',
    submenu: [
      { href: '/used-cars', label: '瀏覽二手車' },
      { href: '/used-cars/compare', label: '車款比較' },
      { href: '/used-cars/saved', label: '已收藏車款' },
    ],
  },
  {
    href: '/dashboard/upload-car',
    label: '賣車',
    submenu: [
      { href: '/dashboard/upload-car', label: '刊登車輛' },
      { href: '/dashboard/my-listings', label: '我的刊登' },
      { href: '/selling-guide', label: '賣車指南' },
    ],
  },
  {
    href: '/services',
    label: '車週邊服務',
    submenu: [
      { href: '/services', label: '瀏覽服務' },
      { href: '/services/categories', label: '服務分類' },
      { href: '/business', label: '商業招募計畫' },
    ],
  },
  {
    href: '/dashboard',
    label: '控制台',
    submenu: [
      { href: '/dashboard/subscription', label: '訂閱管理' },
      { href: '/dashboard/subscription/plans', label: '訂閱方案' },
      { href: '/dashboard/analytics', label: '數據分析' },
      { href: '/services/1/book', label: '預約服務' },
    ],
  },
  { href: '/license-plate-preview', label: '自訂車牌' },
  {
    href: '/articles',
    label: '資訊中心',
    submenu: [
      { href: '/articles', label: '最新文章' },
      { href: '/articles/categories/guides', label: '購車指南' },
      { href: '/articles/categories/maintenance', label: '保養維修' },
      { href: '/articles/categories/news', label: '汽車新聞' },
    ],
  },
  {
    href: '/about/our-story',
    label: '關於我們',
    submenu: [
      { href: '/about/our-story', label: '我們的故事' },
      { href: '/about-us', label: '公司簡介' },
      { href: '/contact', label: '聯絡我們' },
    ],
  },
];

export function NavbarSafe() {
  const [isOpen, setIsOpen] = useState(false);
  const [isScrolled, setIsScrolled] = useState(false);
  const [activeSubmenu, setActiveSubmenu] = useState<string | null>(null);
  const [isMounted, setIsMounted] = useState(false);
  const { data: session, status } = useSession();
  const user = session?.user;

  // Only run client-side effects after mount
  useEffect(() => {
    setIsMounted(true);
  }, []);

  useEffect(() => {
    if (!isMounted) return;
    
    const handleScroll = () => {
      setIsScrolled(window.scrollY > 10);
    };

    window.addEventListener('scroll', handleScroll, { passive: true });
    return () => window.removeEventListener('scroll', handleScroll);
  }, [isMounted]);

  const handleMouseEnter = (href: string) => {
    setActiveSubmenu(href);
  };

  const handleMouseLeave = () => {
    setActiveSubmenu(null);
  };

  const renderAuthSection = () => {
    if (!isMounted || status === 'loading') return null;
    if (session && user) {
      return (
        <MobileUserMenu user={user} onItemClick={() => setIsOpen(false)} />
      );
    }
    return null; // Login button is now in the header for mobile
  };

  // Don't render until mounted to avoid hydration mismatches
  if (!isMounted) {
    return (
      <header className="fixed inset-x-0 top-0 z-50 bg-white/80 shadow-lg backdrop-blur-md">
        <div className="mx-auto flex max-w-7xl items-center justify-between p-4 sm:px-6 lg:px-8">
          <Link href="/" className="group relative flex items-center gap-2 transition-all">
            <span className="text-2xl font-bold text-gray-900">
              PairCar
            </span>
          </Link>
          <nav className="hidden items-center space-x-1 md:flex">
            {menuItems.map((item) => (
              <Link
                key={item.href}
                href={item.href}
                className="group relative rounded-lg px-3 py-2 text-sm font-medium transition-all hover:bg-gray-100/50 text-gray-700"
              >
                {item.label}
              </Link>
            ))}
          </nav>
          <button
            type="button"
            className="rounded-lg p-2 transition-colors md:hidden text-gray-600 hover:bg-gray-100"
          >
            <Menu className="h-6 w-6" />
          </button>
        </div>
      </header>
    );
  }

  return (
    <header
      className={`fixed inset-x-0 top-0 z-50 transition-all duration-300${
        isScrolled
          ? ' bg-white/80 shadow-lg backdrop-blur-md'
          : ' bg-transparent'
      }`}
    >
      <div className="mx-auto flex max-w-7xl items-center justify-between p-4 sm:px-6 lg:px-8">
        {/* Logo */}
        <Link
          href="/"
          className="group relative flex items-center gap-2 transition-all"
        >
          <span
            className={`text-2xl font-bold transition-colors ${
              isScrolled ? 'text-gray-900' : 'text-white'
            }`}
          >
            PairCar
          </span>
          <div className="absolute -bottom-1 left-0 h-0.5 w-0 bg-primaryColor transition-all group-hover:w-full" />
        </Link>

        {/* Desktop Navigation */}
        <nav className="hidden items-center space-x-1 md:flex">
          {menuItems.map((item) => (
            <div
              key={item.href}
              className="relative"
              onMouseEnter={() => handleMouseEnter(item.href)}
              onMouseLeave={handleMouseLeave}
            >
              <Link
                href={item.href}
                className={`group relative rounded-lg px-3 py-2 text-sm font-medium transition-all hover:bg-gray-100/50 ${
                  isScrolled ? 'text-gray-700' : 'text-white'
                }`}
              >
                {item.label}
                <span className="absolute bottom-1.5 left-3 h-px w-0 bg-primaryColor transition-all group-hover:w-[calc(100%-24px)]" />
              </Link>
              {item.submenu && activeSubmenu === item.href && (
                <div className="absolute left-0 mt-1 w-48 rounded-lg bg-white py-2 shadow-lg">
                  {item.submenu.map((subItem) => (
                    <Link
                      key={subItem.href}
                      href={subItem.href}
                      className="block px-4 py-2 text-sm text-gray-700 hover:bg-gray-50"
                    >
                      {subItem.label}
                    </Link>
                  ))}
                </div>
              )}
            </div>
          ))}
          {status !== 'loading' &&
            (session ? (
              <UserMenu />
            ) : (
              <Link
                href="/login"
                className="ml-4 rounded-full bg-primaryColor px-4 py-2 text-sm font-medium text-white shadow-md transition-all hover:bg-primaryColor/90 hover:shadow-lg active:scale-95"
              >
                登入
              </Link>
            ))}
        </nav>

        {/* Mobile Menu Button and Login */}
        <div className="flex items-center gap-2 md:hidden">
          {status !== 'loading' && (
            <>
              {session && user ? (
                <UserMenu />
              ) : (
                <Link
                  href="/login"
                  className={`rounded-full px-3 py-1.5 text-sm font-medium shadow-md transition-all hover:shadow-lg active:scale-95 ${
                    isScrolled
                      ? 'bg-primaryColor text-white hover:bg-primaryColor/90'
                      : 'bg-white text-primaryColor hover:bg-gray-50'
                  }`}
                >
                  登入
                </Link>
              )}
            </>
          )}
          <button
            type="button"
            onClick={() => setIsOpen(!isOpen)}
            className={`rounded-lg p-2 transition-colors ${
              isScrolled
                ? 'text-gray-600 hover:bg-gray-100'
                : 'text-white hover:bg-white/10'
            }`}
          >
            <span className="sr-only">Open menu</span>
            {isOpen ? <X className="h-6 w-6" /> : <Menu className="h-6 w-6" />}
          </button>
        </div>
      </div>

      {/* Mobile Navigation */}
      <AnimatePresence>
        {isOpen && (
          <motion.div
            initial={{ opacity: 0, height: 0 }}
            animate={{ opacity: 1, height: 'auto' }}
            exit={{ opacity: 0, height: 0 }}
            transition={{ duration: 0.2, ease: 'easeInOut' }}
            className="border-t border-gray-200 bg-white shadow-lg md:hidden"
          >
            <div className="max-h-[calc(100vh-80px)] overflow-y-auto">
              <div className="space-y-1 px-3 py-4">
                {menuItems.map((item, index) => (
                  <motion.div
                    key={item.href}
                    initial={{ opacity: 0, x: -20 }}
                    animate={{ opacity: 1, x: 0 }}
                    transition={{ delay: index * 0.05 }}
                    className={index > 0 ? 'border-t border-gray-100 pt-1' : ''}
                  >
                    <Link
                      href={item.href}
                      className="flex items-center justify-between rounded-lg px-4 py-3 text-base font-medium text-gray-700 transition-all hover:bg-gray-50 active:bg-gray-100"
                      onClick={() => setIsOpen(false)}
                    >
                      <span>{item.label}</span>
                      {item.submenu && (
                        <svg className="h-4 w-4 text-gray-400" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5l7 7-7 7" />
                        </svg>
                      )}
                    </Link>
                    {item.submenu && (
                      <div className="ml-3 space-y-0.5 pb-1">
                        {item.submenu.map((subItem) => (
                          <Link
                            key={subItem.href}
                            href={subItem.href}
                            className="block rounded-lg px-4 py-2 pl-8 text-sm text-gray-600 transition-all hover:bg-gray-50 hover:text-gray-900"
                            onClick={() => setIsOpen(false)}
                          >
                            {subItem.label}
                          </Link>
                        ))}
                      </div>
                    )}
                  </motion.div>
                ))}
                {renderAuthSection()}
              </div>
            </div>
          </motion.div>
        )}
      </AnimatePresence>
    </header>
  );
}